import { ServicesApiRequest } from '@/api'
import JobTasksApiRequest from '@/api/job-tasks'
import ServiceApiRequest from '@/api/services'
import { JobExpandablePropertyType, JobsFilterType, JobType, StatusCodeType } from '@/enum'
import {
  CustomFilterTypeExpression,
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListResponseTypeExpression
} from '@/enum/LinqExpressionType'
import JobRequestMapper from '@/mappers/JobRequestMapper'
import { CalendarEvent, Service, ServiceForm } from '@/models'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import { defineStore } from 'pinia'
import usePaginationStore from '../pagination'
import state from './state'
import type { IExplicitlyUpdatedJobProperty } from '@/interfaces'
import type { AxiosResponse } from 'axios'
import { fetchUntilExhaustion, generateFilterExpressionForCalendarEvents } from '@/utils/helpers'
import { expandedProperties, JOB_DETAILS_EXPAND_PARAMS, MESSAGE_CONSTANTS } from '@/utils/constants'
import ErrorType from '@/enum/ErrorType'
import useGlobalStore from '../global'
import NotificationType from '@/enum/NotificationType'

const useServicesStore = defineStore('services', {
  state: () => state,
  actions: {
    async getServices(storeName: string, responseOptions?: any) {
      const servicesApi = new ServicesApiRequest()
      const servicesList = usePaginationStore(storeName)
      servicesList.handleListAPICall(servicesApi.getServices.bind(servicesApi), Service, {
        ...responseOptions,
        [ListResponseTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
      })
    },
    async getServicesForCalendar(start: string, end: string, options?: any) {
      const servicesApi = new ServicesApiRequest()
      const responseOptions = generateFilterExpressionForCalendarEvents(
        JobType.MAINTENANCE,
        start,
        end,
        options
      )
      const result = await fetchUntilExhaustion(
        servicesApi.getServicesTasks.bind(servicesApi),
        responseOptions
      )
      return result
        .map((job) => job)
        .filter((job) => job.scheduledAt || job.dueAt || job.startedAt || job.completedAt)
    },
    async createService(): Promise<AxiosResponse> {
      this.isServiceFormLoading = true
      const servicesApi = new ServicesApiRequest()
      const jobMapper = new JobRequestMapper()
      const globalStore = useGlobalStore()
      const newJob = jobMapper.toJobUpdate({ ...this.serviceForm, type: JobType.MAINTENANCE })
      const result = await servicesApi.createService(newJob?.jobRequestData, expandedProperties)

      try {
        if (result.status === StatusCodeType.Success && result.data) {
          // Prompt user
          globalStore.addToastNotification({
            id: Symbol('Job added'),
            message: `${result.data?.title} added successfully`,
            type: NotificationType.Success
          })
        }
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      } finally {
        this.isServiceFormLoading = false
      }
      return result
    },
    async getService(id: string) {
      this.isServiceFormLoading = true

      try {
        const servicesApi = new ServicesApiRequest(false, true)
        const result = await servicesApi.getService({
          id,
          ...expandedProperties
        })
        if (result.status === StatusCodeType.Success && result.data) {
          this.serviceForm = new ServiceForm(result.data)

          if (!this.serviceForm.isRecurring()) {
            const jobTasksApi = new JobTasksApiRequest()
            const tasksResponse = await jobTasksApi.getJobTasks({ jobId: result.data.id })
            if (
              tasksResponse.status === StatusCodeType.Success &&
              tasksResponse.data?.value?.length
            ) {
              this.oneTimeServiceTask = tasksResponse.data.value[0]
              this.serviceForm.dueDate = this.oneTimeServiceTask?.dueAt
            }
          }
        }
        return result
      } catch (error: any) {
        const title = error?.response.data?.detail

        const globalStore = useGlobalStore()
        globalStore.addError(
          {
            title
          },
          ErrorType.Network
        )
        return error
      } finally {
        this.isServiceFormLoading = false
      }
    },
    async updateService(
      id: string,
      explicitlyUpdatedServiceProperty?: IExplicitlyUpdatedJobProperty
    ) {
      const servicesApi = new ServiceApiRequest(false, true)
      const jobMapper = new JobRequestMapper()
      const newJob = jobMapper.toJobUpdate({ ...this.serviceForm, type: JobType.MAINTENANCE })
      const globalStore = useGlobalStore()
      try {
        const result = await servicesApi.updateService(
          {
            id,
            [ItemTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
          },
          newJob.jobRequestData
        )

        if (result.status === StatusCodeType.Success) {
          const isOneTimeService = !this.serviceForm.isRecurring()
          const hasDueDateChanged = this.serviceForm.dueDate !== this.oneTimeServiceTask?.dueAt

          if (isOneTimeService && hasDueDateChanged && this.oneTimeServiceTask?.id) {
            const jobTasksApi = new JobTasksApiRequest()
            await jobTasksApi.updateJobTask(
              { jobId: id, taskId: this.oneTimeServiceTask.id },
              {
                dueAt: this.serviceForm.dueDate,
                status: this.oneTimeServiceTask.status,
                updatedAt: this.oneTimeServiceTask.updatedAt
              }
            )
          }

          if (result.data) {
            globalStore.addToastNotification({
              id: Symbol('Job updated'),
              message: `${result.data?.title} updated successfully`,
              type: NotificationType.Success
            })
          }
        }

        return result
      } catch (error: any) {
        const title =
          error?.response.data?.detail ?? MESSAGE_CONSTANTS.SERVICE_WAS_DELETED_ALERT_TITLE
        if ([StatusCodeType.NotFound, StatusCodeType.Invalid].includes(error.status)) {
          globalStore.addError(
            {
              title
            },
            ErrorType.Network
          )
          return error
        }
        ErrorHandlerService.handleError(error, error)
      }
    },
    async deleteServices(storeName: string) {
      const servicesListStore = usePaginationStore(storeName)
      try {
        const servicesApi = new ServiceApiRequest()
        if (!servicesListStore.selectedItems) throw new Error('No services to delete')
        const result = await servicesApi.deleteServices({
          [DeleteTypeExpression.Expression]: { Id: servicesListStore.selectedItems }
        })
        return result
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      }
    },
    resetServiceForm() {
      this.serviceForm = new ServiceForm()
      this.oneTimeServiceTask = null
    },
    async getServicesUnshceduledJobs(storeName: string) {
      const unscheduleJobsListStore = usePaginationStore(storeName)
      const jobsApi = new ServiceApiRequest()
      const expandableResources = [
        JobExpandablePropertyType.ASSIGNED_EMPLOYEES,
        JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE,
        JobExpandablePropertyType.JOB
      ]

      const responseOptions = {
        [ListResponseTypeExpression.Expand]: expandableResources.join(','),
        [ListFilterTypeExpression.Filter]: {
          [CustomFilterTypeExpression.Expression]: `(${JobsFilterType.NEXT_TRIGGER_INFO_DUE_AT}==null && ${JobsFilterType.START_DATE}==null)`,
          [JobsFilterType.JOB_TYPE]: JobType.MAINTENANCE
        }
      }

      unscheduleJobsListStore.loading = true

      try {
        await unscheduleJobsListStore.handleListAPICall<CalendarEvent>(
          jobsApi.getServicesTasks.bind(jobsApi),
          CalendarEvent,
          responseOptions
        )
        return true
      } catch (error) {
        return false
      } finally {
        unscheduleJobsListStore.loading = false
      }
    }
  }
})

export default useServicesStore
